// The project-open gate (R40, R47; DQ1, DQ5, DQ17). openProject decides, before the editor mounts, whether a project can open or which of the six
// screens to show, and reads what the editor starts from: the project, its map packs, the listing of one pack and the calibration. It is a plain
// module with no React in it: the caller supplies the display of a screen (`show`), the toast (`notify`) and, in tests, the repository calls
// (`api`) and the poll.
import * as Sentry from '@sentry/browser';
import sortObjectsArray from 'sort-objects-array';
import Config, {CONVERTING_GET_TIMEOUT_MS} from 'config';
import {ApiError, NoResponseError} from 'repository/errors';
import * as ProjectRepository from 'repository/ProjectRepository';
import {hasSettled, IN_FLIGHT_STATES, isConverted, pollStatusUntil} from 'util/poll';

// The repository calls the flow makes.
const defaultApi = {
    getProject: projectId => ProjectRepository.getProject(projectId),
    getStatus: projectId => ProjectRepository.getStatus(projectId),
    getMapPacks: projectId => ProjectRepository.getProjectMapPacks(projectId),
    getFolders: (projectId, pack, options) => ProjectRepository.getProjectMapPackFolders(projectId, pack, options),
    getCalibration: projectId => ProjectRepository.getCalibration(projectId),
};

// The two waits of a converting read: the screen that shows while it lasts, and the /status condition that ends it (DQ1).
//   converting: the project is on the row store, another request holds its lock, or the read got no answer. It ends at storeVersion 2.
//   detecting: the pipeline has not settled, after an admin re-detection (REQ E38) or on a conversion skipped as project_not_settled.
//     It ends when the state leaves the in-flight set.
const WAITS = {
    converting: {screen: 'BeingPrepared', until: isConverted},
    detecting: {screen: 'Processing', until: hasSettled},
};

// The first of the pack asked for (when there is one), tuner and full that the project lists, else null.
const choosePack = (mapPacks, asked) => [asked, 'tuner', 'full'].find(name => name && mapPacks.some(pack => pack.name === name)) ?? null;

// The finding codes a project_conversion_failed message names. The API words it "Project <id> could not be converted to the file store
// (code, code); it stays on the row store" and leaves the parentheses out when it names none (ConversionFailed::forProject).
const findingsOf = error => {
    const named = /\(([a-z0-9_]+(?:,\s*[a-z0-9_]+)*)\)/.exec(error.message);
    return named ? named[1].split(/,\s*/) : [];
};

// What a 409 from a converting route means for the flow (API 0.4): a wait ({wait}), a screen to stop on ({stop}) or nothing the flow maps (null).
const conflictPlan = error => {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return null;
    }
    if (error.is('project_locked')) {
        return {wait: 'converting'};
    }
    if (error.is('project_not_converted')) {
        switch (error.reason) {
            case 'store_version':
                return {wait: 'converting'};
            case 'missing_detection':
                return {wait: 'detecting'};
            case 'missing_upload':
                return {stop: 'ProcessingError', code: 'missing_upload'};
            default:
                return null;
        }
    }
    if (error.is('project_conversion_failed')) {
        const findings = findingsOf(error);
        return findings.includes('project_not_settled') ? {wait: 'detecting'} : {stop: 'ConversionFailed', findings};
    }
    return null;
};

// The same, for the converting listing GET, which also gets no answer when it outlasts its timeout (DQ1).
const readPlan = error => (error instanceof NoResponseError ? {wait: 'converting'} : conflictPlan(error));

// A failed request: the typed errors of the interceptor. Anything else is a bug and is left to surface.
const isRequestFailure = error => error instanceof ApiError || error instanceof NoResponseError;

const isUnknownPack = error => error instanceof ApiError && error.status === 400 && error.is('unknown_map_pack');

// One Sentry event per failed conversion, with the project and the finding codes. The installed SDK's captureMessage takes (message, level) and
// no context, so they go on a scope. The axios error, which carries the Authorization header, is never sent.
const reportConversionFailed = (projectId, findings) => {
    Sentry.withScope(scope => {
        scope.setExtras({projectId, findings});
        Sentry.captureMessage('project_conversion_failed');
    });
};

// One Sentry event for a wait that is shown with no wait. The map packs of step 4 and the calibration of step 7 can be neither polled nor
// re-issued, so a project that keeps answering one of these 409s would loop on its taking-longer screen with nothing recorded: the API does not
// log a 409 project_not_converted. `route` names the read; the code and the reason are the error's own, null when it has none.
const reportUnwaitedConflict = (projectId, route, error) => {
    Sentry.withScope(scope => {
        scope.setExtras({projectId, route, code: error.code, reason: error.reason});
        Sentry.captureMessage('project_open_unwaited_conflict');
    });
};

/**
 * Opens a project in eight steps (numbered as in the 2E-FE plan's Task 4):
 *   1 the project; a 403 or 404 sends the page to the dashboard
 *   2 an in-flight tunerStatus shows Processing and polls /status until the state settles
 *   3 rejected shows NotSupported, error shows ProcessingError
 *   4 the map packs, sorted as the legacy editor sorted them, and the pack to read; none shows NoMapPacks. For a v2 project this is the first call
 *     that opens its session, so a 409 here is answered as step 7 answers one (stopOnConflict): a stop on its screen, a wait on its taking-longer
 *     screen with one Sentry message, and no poll and no second request
 *   5 a store_version 1 project shows BeingPrepared, because the listing GET below converts it
 *   6 the listing: at most one more GET after a wait, and none after a terminal screen. Each retry would run a whole conversion.
 *   7 the calibration; a 409 here is answered as step 4's is
 *   8 the five values
 * show({name, props}) displays a screen. Nothing clears one: the caller does, when the flow resolves with the editor's data.
 * notify(copyKey) asks the caller for a toast from the copy table.
 *
 * Resolves {project, mapPack, mapPacks, folderStructure, calibration} when the editor can open, else {screen}: the flow stopped, and `screen` is
 * the {name, props} it showed, or null when it sent the page to the dashboard instead. Anything the flow does not map is thrown as it came.
 */
export const openProject = async ({
    projectId,
    mapPack,
    api = defaultApi,
    poll = pollStatusUntil,
    show,
    notify,
    timeouts = {convertingGetMs: CONVERTING_GET_TIMEOUT_MS},
}) => {
    // Shows a screen, and answers what the flow resolves with when it stops on that screen.
    const display = (name, props = {}) => {
        const screen = {name, props};
        show(screen);
        return {screen};
    };
    const pollStatus = until => poll(() => api.getStatus(projectId), until);

    // The screen a plan stops on. A failed conversion is reported once, where the flow stops on it.
    const stopOn = plan => {
        if (plan.stop === 'ConversionFailed') {
            const {findings} = plan;
            reportConversionFailed(projectId, findings);
            return display('ConversionFailed', {
                projectId,
                code: findings.length ? `project_conversion_failed (${findings.join(', ')})` : 'project_conversion_failed',
                ...(findings.includes('no_detection') && {reupload: true}),
            });
        }
        return display(plan.stop, {projectId, code: plan.code});
    };

    // A 409 from a read that gets no wait and no second request: the map packs of step 4 and the calibration of step 7, named by `route`. A stop
    // shows its screen. A wait goes straight to its taking-longer screen, the one with a Retry, and sends one Sentry message: the API logs a 409 project_locked (its
    // project_lock channel) but does not log a 409 project_not_converted, so a project that keeps answering that one would loop here with nothing
    // recorded. Anything else is thrown as it came. Step 6 classifies the same errors with conflictPlan, but it waits and re-issues.
    const stopOnConflict = (error, route) => {
        const plan = conflictPlan(error);
        if (plan === null) {
            throw error;
        }
        if (plan.stop) {
            return stopOn(plan);
        }
        reportUnwaitedConflict(projectId, route, error);
        return display(WAITS[plan.wait].screen, {takingLonger: true});
    };

    // 1
    let project;
    try {
        project = await api.getProject(projectId);
    } catch (error) {
        // A 401 was redirected by the interceptor. A project that is not the account's, or does not exist, goes back to the dashboard, as every
        // load error did before.
        if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
            window.location = Config.dashboard;
            return {screen: null};
        }
        throw error;
    }

    // 3, as a function because a wait for a re-detection ends in the same question.
    const unopenable = (state, customerMessage) => {
        if (state === 'rejected') {
            return display('NotSupported', {message: customerMessage || project.customerMessage || null});
        }
        if (state === 'error') {
            return display('ProcessingError', {projectId, code: 'error'});
        }
        // ready and archived open; an archived v2 project is editable (API 3.10).
        return null;
    };

    // 2
    let state = project.tunerStatus;
    let customerMessage = null;
    if (IN_FLIGHT_STATES.has(project.tunerStatus)) {
        display('Processing');
        const polled = await pollStatus(hasSettled);
        if (!polled.done) {
            return display('Processing', {takingLonger: true});
        }
        ({state, message: customerMessage} = polled.status);
    }

    // 3
    const blocked = unopenable(state, customerMessage);
    if (blocked) {
        return blocked;
    }

    // 4: for a v2 project this opens the session, so it answers the 409s of a missing upload or detection before step 6 can
    let listedPacks;
    try {
        listedPacks = await api.getMapPacks(projectId);
    } catch (error) {
        return stopOnConflict(error, 'map-packs');
    }
    const mapPacks = sortObjectsArray(listedPacks, 'mapCount');
    const firstPack = choosePack(mapPacks, mapPack);
    if (firstPack === null) {
        return display('NoMapPacks');
    }

    // 5
    if (project.storeVersion === 1) {
        display('BeingPrepared');
    }

    // 6: {folderStructure, mapPack}, or {screen} when the flow stopped.
    const readListing = async () => {
        let pack = firstPack;
        const read = async () => ({folderStructure: await api.getFolders(projectId, pack, {timeout: timeouts.convertingGetMs}), mapPack: pack});
        let failure;
        try {
            return await read();
        } catch (error) {
            failure = error;
        }
        // The listing named a pack the project then refused to read: show the full one, and say so once.
        if (isUnknownPack(failure) && pack !== 'full') {
            pack = 'full';
            notify('unknown_map_pack');
            try {
                return await read();
            } catch (error) {
                failure = error;
            }
        }

        const plan = readPlan(failure);
        if (plan === null) {
            throw failure;
        }
        if (plan.stop) {
            return stopOn(plan);
        }
        // A wait, then one more GET. Each retry would run a whole conversion, so a second failure of any kind ends the flow on the
        // taking-longer screen, for the user to retry.
        const {screen, until} = WAITS[plan.wait];
        display(screen);
        const polled = await pollStatus(until);
        if (!polled.done) {
            return display(screen, {takingLonger: true});
        }
        if (plan.wait === 'detecting') {
            const blocked = unopenable(polled.status.state, polled.status.message);
            if (blocked) {
                return blocked;
            }
        }
        try {
            return await read();
        } catch (error) {
            if (isRequestFailure(error)) {
                return display(screen, {takingLonger: true});
            }
            throw error;
        }
    };
    const listing = await readListing();
    if ('screen' in listing) {
        return listing;
    }

    // 7
    let calibration;
    try {
        calibration = await api.getCalibration(projectId);
    } catch (error) {
        return stopOnConflict(error, 'calibration-data');
    }

    // 8
    return {
        project,
        mapPack: listing.mapPack,
        mapPacks,
        folderStructure: sortObjectsArray(listing.folderStructure, 'titleDescription'),
        calibration,
    };
};
